import React from 'react';
import { Home as HomeIcon, Camera, BellRing, Smartphone, Lightbulb, Thermometer, Car, KeyRound } from 'lucide-react';
import { CTAButton, PageHero, SectionLabel } from '../components/shared';
const features = [{
  icon: BellRing,
  title: 'Smart alarms',
  text: 'Door, window, motion, and glass-break sensors with optional 24/7 monitoring.'
}, {
  icon: Camera,
  title: 'Cameras and doorbells',
  text: 'See who is at the door, in the driveway, or in the backyard — day or night.'
}, {
  icon: KeyRound,
  title: 'Smart locks',
  text: 'Lock and unlock from anywhere. Custom codes for guests, kids, and cleaners.'
}, {
  icon: Car,
  title: 'Garage control',
  text: 'Open, close, and get alerts if the garage is left open. Tied to your alarm system.'
}, {
  icon: Lightbulb,
  title: 'Lighting and scenes',
  text: 'Schedule lights, link them to your security system, and never come home to a dark house.'
}, {
  icon: Thermometer,
  title: 'Climate and water',
  text: 'Smart thermostats, water sensors, and energy automation that pays you back.'
}];
export function SmartHome() {
  return <>
      <PageHero eyebrow="Residential stays strong" title="Smart home security from the same local team." subtitle="Powered by Alarm.com — alarms, cameras, locks, lights, garage, and climate, all in one app, supported by a real Northern California team." data-id="element-513">
        <div className="flex flex-wrap gap-3" data-id="element-514">
          <CTAButton to="/assessment" data-id="element-515">Free home assessment</CTAButton>
          <CTAButton variant="secondary" href="https://www.alarm.com/US/watchdogsmarthome?home=1" data-id="element-516">
            Customer login
          </CTAButton>
        </div>
      </PageHero>

      <section className="mx-auto max-w-7xl px-4 py-20 sm:px-6 lg:px-8" data-id="element-517">
        <SectionLabel data-id="element-518">What is included</SectionLabel>
        <h2 className="text-4xl font-black tracking-tight sm:text-5xl" data-id="element-519">
          One app for the whole home.
        </h2>
        <div className="mt-10 grid gap-5 md:grid-cols-2 lg:grid-cols-3" data-id="element-520">
          {features.map(f => {
          const Icon = f.icon;
          return <div key={f.title} className="rounded-[2rem] border border-slate-200 bg-slate-50 p-7" data-id="element-521">
                <Icon className="mb-5 h-7 w-7 text-sky-500" data-id="element-522" />
                <h3 className="text-xl font-black" data-id="element-523">{f.title}</h3>
                <p className="mt-3 leading-7 text-slate-600" data-id="element-524">{f.text}</p>
              </div>;
        })}
        </div>
      </section>

      <section className="bg-white py-20" data-id="element-525">
        <div className="mx-auto grid max-w-7xl gap-12 px-4 sm:px-6 lg:grid-cols-[.95fr_1.05fr] lg:px-8 lg:items-center" data-id="element-526">
          <div className="rounded-[2rem] bg-gradient-to-br from-sky-100 via-white to-slate-100 p-6 shadow-xl ring-1 ring-slate-200" data-id="element-527">
            <div className="rounded-[1.5rem] bg-white p-6 shadow-sm" data-id="element-528">
              <div className="mb-6 flex items-center justify-between" data-id="element-529">
                <div data-id="element-530">
                  <p className="text-sm font-black text-slate-950" data-id="element-531">
                    Smart Home Dashboard
                  </p>
                  <p className="text-xs text-slate-500" data-id="element-532">
                    Protected by Watchdog
                  </p>
                </div>
                <div className="rounded-full bg-emerald-100 px-3 py-1 text-xs font-black text-emerald-700" data-id="element-533">
                  Armed Stay
                </div>
              </div>
              <div className="grid gap-4 sm:grid-cols-2" data-id="element-534">
                {([[HomeIcon, 'Front Door', 'Locked'], [Camera, 'Driveway', 'Live'], [BellRing, 'Alarm', 'Ready'], [Smartphone, 'Mobile App', 'Connected']] as const).map(([Icon, title, status]) => <div key={title} className="rounded-3xl bg-slate-50 p-5 ring-1 ring-slate-200" data-id="element-535">
                    <Icon className="mb-5 h-7 w-7 text-sky-500" data-id="element-536" />
                    <p className="font-black" data-id="element-537">{title}</p>
                    <p className="mt-1 text-sm text-slate-500" data-id="element-538">{status}</p>
                  </div>)}
              </div>
            </div>
          </div>
          <div data-id="element-539">
            <SectionLabel data-id="element-540">Why homeowners pick Watchdog</SectionLabel>
            <h2 className="text-4xl font-black tracking-tight sm:text-5xl" data-id="element-541">
              Real people. Real installs. Real support.
            </h2>
            <p className="mt-6 text-lg leading-8 text-slate-600" data-id="element-542">
              No call centers in another country. We design your system, install
              it cleanly, walk you through how to use it, and stay available
              when you need us.
            </p>
            <div className="mt-8 flex flex-wrap gap-3" data-id="element-543">
              <CTAButton to="/assessment" data-id="element-544">Schedule a home consult</CTAButton>
              <CTAButton variant="dark" href="tel:+19168424729" data-id="element-545">
                Call (916) 842-4729
              </CTAButton>
            </div>
          </div>
        </div>
      </section>
    </>;
}